<template>
  <div v-if="weeks.length" class="flex flex-col items-center gap-2">
    <div
      class="grid gap-[2px] w-full"
      :style="{ gridTemplateColumns: `repeat(${weeks.length}, 1fr)`, gridTemplateRows: 'repeat(7, 1fr)', gridAutoFlow: 'column' }">
      <div
        v-for="cell in cells"
        :key="cell.key"
        class="aspect-square rounded-[2px]"
        :style="{ backgroundColor: cell.color }"
        :title="cell.title"></div>
    </div>
    <div class="flex flex-row items-center gap-1 text-xs text-gray-400 self-end">
      <span>Less</span>
      <div v-for="(c, i) in legend" :key="i" class="w-[10px] h-[10px] rounded-[2px]" :style="{ backgroundColor: c }"></div>
      <span>More</span>
    </div>
  </div>
</template>

<script setup>
import {computed} from "vue";

const props = defineProps({
  scores: {type: Array, default: () => []},
  numWeeks: {type: Number, default: 26}
});

// A "fully saturated" day for the heatmap. The run is effectively endless (soft cap is far
// higher), so shade against a realistic strong day rather than the cap, otherwise typical
// scores would all render near-invisible.
const FULL_DAY_SCORE = 20;

const DAY = 86400000;

// Match the backend's UTC `yyyy-M-d` format (no leading zeros) so keys line up.
function fmt(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}-${d.getUTCDate()}`;
}

function parseKey(s) {
  const [y, m, d] = s.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

const scoreMap = computed(() => {
  const map = new Map();
  for (const s of props.scores ?? []) {
    if (s?.date != null) map.set(fmt(parseKey(s.date)), s.score);
  }
  return map;
});

// Columns are weeks (Sun→Sat top to bottom), ending on the current week.
const weeks = computed(() => {
  const todayUtc = parseKey(fmt(Date.now()));
  const todayDow = new Date(todayUtc).getUTCDay();
  const lastSat = todayUtc + (6 - todayDow) * DAY;
  const firstDay = lastSat - (props.numWeeks * 7 - 1) * DAY;
  const result = [];
  for (let w = 0; w < props.numWeeks; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      const ms = firstDay + (w * 7 + d) * DAY;
      week.push(ms);
    }
    result.push(week);
  }
  return result;
});

function colorFor(score, future) {
  if (future) return 'transparent';
  if (score == null) return 'rgba(255,255,255,0.06)';
  const opacity = 0.25 + (Math.max(0, Math.min(FULL_DAY_SCORE, score)) / FULL_DAY_SCORE) * 0.75;
  return `rgba(66,222,136,${opacity})`;
}

const cells = computed(() => {
  const today = parseKey(fmt(Date.now()));
  return weeks.value.flat().map(ms => {
    const key = fmt(ms);
    const future = ms > today;
    const score = scoreMap.value.get(key);
    return {
      key,
      color: colorFor(score, future),
      title: future ? '' : `${key}: ${score == null ? 'no play' : score}`
    };
  });
});

const legend = [0, FULL_DAY_SCORE / 3, (FULL_DAY_SCORE * 2) / 3, FULL_DAY_SCORE].map(s => colorFor(s, false));
</script>
